import { Controller } from "@hotwired/stimulus"

/**
 * Controller for handling actions within drawers without using Turbo.
 * This prevents Flowbite's drawer/backdrop state from being disrupted.
 *
 * Usage:
 *   <div data-controller="drawer-action" data-drawer-action-content-target="content">
 *     <button data-action="click->drawer-action#submit"
 *             data-drawer-action-url-param="/briefs/123/publish"
 *             data-drawer-action-method-param="post">
 *       Publish
 *     </button>
 *     <div data-drawer-action-target="content">
 *       <!-- Content to be replaced -->
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["content"]

  async submit(event) {
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.drawerActionUrlParam
    const method = (button.dataset.drawerActionMethodParam || "post").toUpperCase()

    // Disable button while loading
    button.disabled = true
    const originalText = button.innerHTML
    button.innerHTML = '<span class="animate-pulse">...</span>'

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "Accept": "text/html",
          "X-CSRF-Token": this.csrfToken
        }
      })

      if (response.ok) {
        const html = await response.text()

        // Parse the response and extract the content
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, "text/html")

        // Find the content in the response (it should be the drawer content partial)
        const newContent = doc.body.firstElementChild

        if (newContent && this.hasContentTarget) {
          // Replace the innerHTML of our content target
          this.contentTarget.innerHTML = newContent.innerHTML
        }
      } else {
        console.error("Drawer action failed:", response.status)
        button.innerHTML = originalText
        button.disabled = false
      }
    } catch (error) {
      console.error("Drawer action error:", error)
      button.innerHTML = originalText
      button.disabled = false
    }
  }

  get csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.getAttribute("content") : ""
  }
}
